import { createContext, ReactNode, useContext, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  ClientFormWithCustomFields,
  ClientInputFormWithCustomFields,
} from '@/features/public/booking/schemas/clientSchema.ts';
import {
  BookingQuoteDto,
  PricingErrorDto,
} from '@/features/public/booking/types/BookingQuoteDto.ts';
import { useBookingQuoteEstimate } from '@/features/public/booking/hooks/useGetBookingQuote.ts';
import { getTemporalIdForIndex } from '@/features/shared/booking/utils/temporalId.ts';
import CustomFieldDefinitionDto from '@/features/dashboard/tourCustomFields/types/_CustomFieldDefinitionDto.ts';

interface BookingQuoteContextValue {
  quote?: BookingQuoteDto;
  isFetching: boolean;
  /** Błędy wyceny uczestnika o danym indeksie formularza (korelacja po temporalId). */
  participantErrors: (index: number) => PricingErrorDto[];
}

const emptyQuote: BookingQuoteContextValue = {
  quote: undefined,
  isFetching: false,
  participantErrors: () => [],
};

// Domyślna wartość, a nie `undefined`: `TourOptions` służy też panelowi, gdzie
// żadnej wyceny na żywo nie ma.
const BookingQuoteContext = createContext<BookingQuoteContextValue>(emptyQuote);

export const useBookingQuote = () => useContext(BookingQuoteContext);

export const useParticipantPricingErrors = (index: number): PricingErrorDto[] =>
  useBookingQuote().participantErrors(index);

interface BookingQuoteProviderProps {
  orgSlug: string;
  tourSlug: string;
  participantForm: CustomFieldDefinitionDto[];
  /** Klucz formularza z ostatniej wysyłki — omija opóźnienie, patrz `useBookingQuoteEstimate`. */
  submittedFormKey?: string;
  children: ReactNode;
}

/**
 * Wycena na żywo dla całego kroku 1.
 *
 * Obserwowanie formularza siedzi tutaj, a nie w kartach uczestników: `children`
 * przychodzą gotowe z góry, więc przerysowanie providera po każdym znaku nie
 * schodzi w dół drzewa. Wartość kontekstu zmienia się dopiero razem z
 * odpowiedzią serwera, więc kafelki opcji i pola z komunikatami przerysowują
 * się tylko wtedy, gdy naprawdę jest co pokazać.
 */
export const BookingQuoteProvider = ({
  orgSlug,
  tourSlug,
  participantForm,
  submittedFormKey,
  children,
}: BookingQuoteProviderProps) => {
  const { control } = useFormContext<ClientInputFormWithCustomFields>();
  const clientFormData = useWatch({
    control,
  }) as unknown as ClientFormWithCustomFields;

  const { data, isFetching } = useBookingQuoteEstimate(
    orgSlug,
    tourSlug,
    clientFormData,
    participantForm,
    submittedFormKey,
  );

  const value = useMemo<BookingQuoteContextValue>(
    () => ({
      quote: data,
      isFetching,
      participantErrors: (index: number) =>
        data?.pricesPerParticipants?.find(
          (participant) =>
            participant.temporalId === getTemporalIdForIndex(index),
        )?.errors ?? [],
    }),
    [data, isFetching],
  );

  return (
    <BookingQuoteContext.Provider value={value}>
      {children}
    </BookingQuoteContext.Provider>
  );
};
